import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const GIFT_PROPERTY_KEY = '_brinde';
const GIFT_PROPERTY_VALUE = 'sim';
const GIFT_SOURCE = 'gift-progress';

let giftSyncInFlight = false;
let giftSyncTimeout = null;
let listenersBound = false;

function formatGiftProgressBRL(value) {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function eligibleCartTotalReais(cart) {
  if (!cart?.items) return 0;

  const cents = cart.items.reduce((sum, item) => {
    const isGift = item.properties && item.properties[GIFT_PROPERTY_KEY] === GIFT_PROPERTY_VALUE;
    if (isGift) return sum;
    return sum + (item.final_line_price || 0);
  }, 0);

  return cents / 100;
}

function applyGiftProgress(totalReais) {
  const wrappers = document.querySelectorAll('.gift-progress');
  if (wrappers.length === 0) return false;

  wrappers.forEach((wrapper) => {
    const maxAmount = parseFloat(wrapper.dataset.maxAmount);
    if (!maxAmount) return;

    const total = typeof totalReais === 'number' ? totalReais : parseFloat(wrapper.dataset.cartTotal) || 0;
    wrapper.dataset.cartTotal = String(total);

    const fill = wrapper.querySelector('.gift-progress__fill');
    const message = wrapper.querySelector('.gift-progress__message');
    const stageEls = wrapper.querySelectorAll('.gift-progress__stage');

    const progressPct = Math.min((total / maxAmount) * 100, 100);
    if (fill) {
      fill.style.setProperty('--progress', `${progressPct}%`);
    }

    let nextStage = null;
    stageEls.forEach((el) => {
      const amount = parseFloat(el.dataset.amount);
      const isComplete = total >= amount;
      el.classList.toggle('is-complete', isComplete);

      if (!isComplete && !nextStage) {
        nextStage = {
          amount,
          label: el.dataset.giftLabel || '',
        };
      }
    });

    if (!message) return;

    const newMessage = !nextStage
      ? 'Você desbloqueou <strong>todos os presentes</strong>!'
      : `Faltam <strong>R$ ${formatGiftProgressBRL(nextStage.amount - total)}</strong> para ganhar: <strong>${nextStage.label}</strong>`;

    if (message.innerHTML !== newMessage) {
      message.innerHTML = newMessage;
    }
  });

  return true;
}

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : ''))
    .filter(Boolean);
}

function dispatchCartRefresh(cart, sections) {
  document.dispatchEvent(
    new CartUpdateEvent(cart, GIFT_SOURCE, {
      source: GIFT_SOURCE,
      itemCount: cart.item_count,
      sections: sections || {},
    })
  );
}

function scheduleGiftSync() {
  if (giftSyncTimeout) clearTimeout(giftSyncTimeout);
  giftSyncTimeout = setTimeout(runGiftSync, 350);
}

function runGiftSync() {
  if (giftSyncInFlight) return;

  const wrapper = document.querySelector('.gift-progress');
  if (!wrapper) return;

  const stageEls = Array.from(wrapper.querySelectorAll('.gift-progress__stage[data-variant-ids]')).filter(
    (el) => el.dataset.variantIds
  );

  if (stageEls.length === 0) return;

  giftSyncInFlight = true;

  fetch('/cart.js')
    .then((res) => res.json())
    .then((cart) => {
      const total = eligibleCartTotalReais(cart);
      applyGiftProgress(total);

      const toAdd = [];
      const toRemove = [];

      stageEls.forEach((el) => {
        const variantIds = el.dataset.variantIds
          .split(',')
          .map((v) => parseInt(v, 10))
          .filter((id) => !Number.isNaN(id));
        const amount = parseFloat(el.dataset.amount);
        const achieved = total >= amount;

        variantIds.forEach((variantId) => {
          const cartLine = cart.items.find((item) => item.variant_id === variantId);
          const isOurGift = !!(
            cartLine &&
            cartLine.properties &&
            cartLine.properties[GIFT_PROPERTY_KEY] === GIFT_PROPERTY_VALUE
          );

          if (achieved && !cartLine) {
            toAdd.push({
              id: variantId,
              quantity: 1,
              properties: { [GIFT_PROPERTY_KEY]: GIFT_PROPERTY_VALUE },
            });
          } else if (!achieved && isOurGift) {
            toRemove.push(variantId);
          }
        });
      });

      if (toAdd.length === 0 && toRemove.length === 0) {
        giftSyncInFlight = false;
        return;
      }

      const sectionIds = getCartSectionIds();
      const payloadExtras = {
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      };

      const removePromise =
        toRemove.length > 0
          ? fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                updates: toRemove.reduce((acc, id) => {
                  acc[id] = 0;
                  return acc;
                }, {}),
                ...payloadExtras,
              }),
            }).then((res) => res.json())
          : Promise.resolve(null);

      return removePromise
        .then((removeResult) => {
          if (toAdd.length === 0) return removeResult;
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: toAdd,
              ...payloadExtras,
            }),
          }).then((res) => res.json());
        })
        .then((result) => {
          const sections = result?.sections || {};
          return fetch('/cart.js')
            .then((res) => res.json())
            .then((freshCart) => {
              applyGiftProgress(eligibleCartTotalReais(freshCart));
              dispatchCartRefresh(freshCart, sections);
            });
        });
    })
    .catch((err) => {
      console.error('[gift-progress] erro ao sincronizar presentes:', err);
    })
    .finally(() => {
      giftSyncInFlight = false;
    });
}

function refreshFromCartJs() {
  fetch('/cart.js')
    .then((res) => res.json())
    .then((freshCart) => {
      applyGiftProgress(eligibleCartTotalReais(freshCart));
      scheduleGiftSync();
    })
    .catch(() => {
      applyGiftProgress();
      scheduleGiftSync();
    });
}

function onCartUpdate(event) {
  const cart = event?.detail?.resource;
  if (cart?.items) {
    applyGiftProgress(eligibleCartTotalReais(cart));
  } else {
    applyGiftProgress();
  }

  if (event?.detail?.data?.source === GIFT_SOURCE) return;
  scheduleGiftSync();
}

function bootGiftProgress() {
  const found = applyGiftProgress();
  if (found) {
    scheduleGiftSync();
    return;
  }
  refreshFromCartJs();
}

function initGiftProgress() {
  if (!listenersBound) {
    listenersBound = true;
    document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
    window.addEventListener('pageshow', bootGiftProgress);
  }

  bootGiftProgress();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGiftProgress);
} else {
  initGiftProgress();
}
